

import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import PublicLayout from './pages/public/PublicLayout';
import HomePage from './pages/public/HomePage';
import ProjectDetailPage from './pages/public/ProjectDetailPage';
import TeamPage from './pages/public/TeamPage';
import JournalPage from './pages/public/JournalPage';
import ArticleDetailPage from './pages/public/ArticleDetailPage';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminProjectFormPage from './pages/admin/AdminProjectFormPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminJournalPage from './pages/admin/AdminJournalPage';
import AdminArticleFormPage from './pages/admin/AdminArticleFormPage';
import AdminTeamPage from './pages/admin/AdminTeamPage';
import AdminTeamMemberFormPage from './pages/admin/AdminTeamMemberFormPage';
import AdminDeveloperPage from './pages/admin/AdminDeveloperPage';
import AdminUserManagementPage from './pages/admin/AdminUserManagementPage';
import AdminUserFormPage from './pages/admin/AdminUserFormPage';
import AdminSeederPage from './pages/admin/AdminSeederPage';
import AdminConsultantsPage from './pages/admin/AdminConsultantsPage';
import AdminConsultantFormPage from './pages/admin/AdminConsultantFormPage';
import AdminPagesPage from './pages/admin/AdminPagesPage';
import AdminPageFormPage from './pages/admin/AdminPageFormPage';
import GenericPage from './pages/public/GenericPage';
import { useSettings } from './hooks/useSettings';
import { useAuth } from './hooks/useAuth';
import { ToastProvider } from './hooks/useToast';
import { useCollection } from './hooks/useCollection';
import type { Page } from './types';
import AdminAssociatesPage from './pages/admin/AdminAssociatesPage';
import AdminAssociateFormPage from './pages/admin/AdminAssociateFormPage';
import AssociatesListPage from './pages/public/AssociatesListPage';
import AssociatePortfolioPage from './pages/public/AssociatePortfolioPage';
import AboutUsPage from './pages/public/AboutUsPage';
import ContactUsPage from './pages/public/ContactUsPage';
import OurServicesPage from './pages/public/OurServicesPage';
import AdminAboutPage from './pages/admin/AdminAboutPage';
import AdminServicesPage from './pages/admin/AdminServicesPage';
import AdminServiceFormPage from './pages/admin/AdminServiceFormPage';
import AdminCertificatesPage from './pages/admin/AdminCertificatesPage';
import AdminCertificateFormPage from './pages/admin/AdminCertificateFormPage';
import AdminTestimonialsPage from './pages/admin/AdminTestimonialsPage';
import AdminTestimonialFormPage from './pages/admin/AdminTestimonialFormPage';
import AdminInvitesPage from './pages/admin/AdminInvitesPage';
import VideosPage from './pages/public/VideosPage';
import AdminVideosPage from './pages/admin/AdminVideosPage';
import AdminVideoFormPage from './pages/admin/AdminVideoFormPage';


const App: React.FC = () => {
    const { settings, updateSettings, isLoading: settingsLoading } = useSettings();
    const { currentUser, authLoading, login, logout, loginWithGoogle } = useAuth();
    const { data: pages, isLoading: pagesLoading } = useCollection<Page>('pages', { field: 'order', direction: 'asc' });
    const [projectUpdateCount, setProjectUpdateCount] = useState(0);

    const handleProjectUpdate = () => {
        setProjectUpdateCount(prev => prev + 1);
    };


    if (settingsLoading || authLoading || pagesLoading) {
        return (
            <div className="flex items-center justify-center h-screen bg-brand-light">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-dashed rounded-full animate-spin border-brand-dark"></div>
                    <p className="mt-4 text-lg text-brand-dark">Loading...</p>
                </div>
            </div>
        );
    }

    const PublicRoutes = () => (
        <PublicLayout 
            settings={settings} 
            pages={pages || []} 
            updateSettings={updateSettings}
            onProjectUpdate={handleProjectUpdate}
        >
            <Outlet />
        </PublicLayout>
    );

    const AdminRoutes = () => (
        currentUser ? (
            <AdminLayout onLogout={logout} settings={settings} currentUser={currentUser}>
                <Outlet />
            </AdminLayout>
        ) : <Navigate to="/admin/login" />
    );
    
    const DeveloperRoutes = () => (
        currentUser?.role === 'developer' ? <Outlet /> : <Navigate to="/admin/dashboard" />
    );

    return (
        <ToastProvider>
            <BrowserRouter>
                <Routes>
                    <Route path="/admin/login" element={!currentUser ? <AdminLoginPage settings={settings} onLogin={login} onGoogleLogin={loginWithGoogle} /> : <Navigate to="/admin/dashboard" />} />

                    <Route path="/admin" element={<AdminRoutes />}>
                        <Route path="dashboard" element={<AdminDashboardPage />} />
                        <Route path="pages" element={<AdminPagesPage />} />
                        <Route path="page/new" element={<AdminPageFormPage />} />
                        <Route path="page/edit/:id" element={<AdminPageFormPage />} />
                        <Route path="project/new" element={<AdminProjectFormPage settings={settings} />} />
                        <Route path="project/edit/:id" element={<AdminProjectFormPage settings={settings} />} />
                        <Route path="about" element={<AdminAboutPage />} />
                        <Route path="services" element={<AdminServicesPage />} />
                        <Route path="service/new" element={<AdminServiceFormPage />} />
                        <Route path="service/edit/:id" element={<AdminServiceFormPage />} />
                        <Route path="certificates" element={<AdminCertificatesPage />} />
                        <Route path="certificate/new" element={<AdminCertificateFormPage />} />
                        <Route path="certificate/edit/:id" element={<AdminCertificateFormPage />} />
                        <Route path="testimonials" element={<AdminTestimonialsPage />} />
                        <Route path="testimonial/new" element={<AdminTestimonialFormPage />} />
                        <Route path="testimonial/edit/:id" element={<AdminTestimonialFormPage />} />
                        <Route path="videos" element={<AdminVideosPage />} />
                        <Route path="video/new" element={<AdminVideoFormPage />} />
                        <Route path="video/edit/:id" element={<AdminVideoFormPage />} />
                        {settings.enableJournal && (
                            <>
                                <Route path="journal" element={<AdminJournalPage />} />
                                <Route path="article/new" element={<AdminArticleFormPage />} />
                                <Route path="article/edit/:id" element={<AdminArticleFormPage />} />
                            </>
                        )}
                        <Route path="team" element={<AdminTeamPage />} />
                        <Route path="team/new" element={<AdminTeamMemberFormPage />} />
                        <Route path="team/edit/:id" element={<AdminTeamMemberFormPage />} />
                        <Route path="consultants" element={<AdminConsultantsPage />} />
                        <Route path="consultant/new" element={<AdminConsultantFormPage />} />
                        <Route path="consultant/edit/:id" element={<AdminConsultantFormPage />} />
                        <Route path="associates" element={<AdminAssociatesPage />} />
                        <Route path="associate/new" element={<AdminAssociateFormPage />} />
                        <Route path="associate/edit/:id" element={<AdminAssociateFormPage />} />
                        <Route path="settings" element={<AdminSettingsPage settings={settings} onSave={updateSettings} />} />
                        
                        {/* Developer Only Routes */}
                        <Route element={<DeveloperRoutes />}>
                            <Route path="users" element={<AdminUserManagementPage />} />
                            <Route path="user/new" element={<AdminUserFormPage />} />
                            <Route path="invites" element={<AdminInvitesPage />} />
                            <Route path="developer" element={<AdminDeveloperPage settings={settings} onSave={updateSettings} />} />
                            <Route path="seeder" element={<AdminSeederPage />} />
                        </Route>
                        
                        <Route index element={<Navigate to="dashboard" />} />
                    </Route>

                    <Route path="/" element={<PublicRoutes />}>
                        <Route index element={<HomePage key={projectUpdateCount} settings={settings} />} />
                        <Route path="about" element={<AboutUsPage settings={settings} />} />
                        <Route path="services" element={<OurServicesPage settings={settings} />} />
                        <Route path="contact" element={<ContactUsPage settings={settings} />} />
                        <Route path="videos" element={<VideosPage settings={settings} />} />
                        <Route path="page/:slug" element={<GenericPage settings={settings} />} />
                        <Route path="team" element={<TeamPage settings={settings} />} />
                        <Route path="associates" element={<AssociatesListPage settings={settings} />} />
                        <Route path="associates/:slug" element={<AssociatePortfolioPage key={projectUpdateCount} settings={settings} />} />
                        {settings.enableJournal && (
                            <>
                                <Route path="journal" element={<JournalPage settings={settings} />} />
                                <Route path="journal/:slug" element={<ArticleDetailPage settings={settings} />} />
                            </>
                        )}
                        <Route path="project/:slug" element={<ProjectDetailPage settings={settings} />} />
                        <Route path="*" element={<Navigate to="/" />} />
                    </Route>
                </Routes>
            </BrowserRouter>
        </ToastProvider>
    );
};

export default App;